import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddCircleOutline as EntreeIcon,
  CheckCircleOutline as ComptageIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  InfoOutlined as InfoIcon,
  LockOutlined as LockIcon,
  OpenInNew as OpenIcon,
  PrintOutlined as PrintIcon,
  RemoveCircleOutline as SortieIcon,
  Savings as OuvertureIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { comptoirCaisseAPI } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { useComptoirCaisseMeta } from '../../../hooks/queries/useComptoir';
import { ComptoirCaisseImpact, ComptoirCaisseMove, ComptoirCaisseOuverture } from '../../../types/comptoir';
import { COLOR, NumberField, dh, fDate, num, primaryBtnSx, r2, today } from '../ui';

const GREEN = '#2E7D32';
const RED = '#D32F2F';
const ORANGE = '#ED6C02';

const Head: React.FC<{ icon: React.ReactNode; title: string; sub: string; color: string; id: string; onClose: () => void }> = ({ icon, title, sub, color, id, onClose }) => (
  <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
    <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha(color, 0.12), color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography id={id} variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary">{sub}</Typography>
    </Box>
    <IconButton onClick={onClose} aria-label="Fermer"><CloseIcon /></IconButton>
  </Box>
);

/** "Solde actuel 1 250,00 DH" line shown at the top of the cash dialogs. */
const SoldeLine: React.FC<{ label: string; sub: string; value: string }> = ({ label, sub, value }) => (
  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', bgcolor: alpha(COLOR, 0.04) }}>
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Box>
    <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{value}</Typography>
  </Paper>
);

/* ---------------- Entrée / Sortie (new, or `op` to modify) ---------------- */
export const CaisseOperationDialog: React.FC<{
  open: boolean; sens: 'entree' | 'sortie'; solde: number; depuis: string | null; op?: ComptoirCaisseMove | null;
  onClose: () => void; onSaved: (x: ComptoirCaisseMove, print: boolean) => void;
}> = ({ open, sens, solde, depuis, op, onClose, onSaved }) => {
  const E = sens === 'entree';
  const { data: meta } = useComptoirCaisseMeta(open);
  const [f, setF] = useState({ montant: '', categorie: '', date: today(), motif: '', beneficiaire: '', piece_ref: '', service_id: '' });
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setF(op
        ? {
          montant: String(num(op.montant)), categorie: op.categorie, date: op.date, motif: op.motif || '',
          beneficiaire: op.beneficiaire || '', piece_ref: op.piece_ref || '', service_id: op.service_id ? String(op.service_id) : '',
        }
        : { montant: '', categorie: '', date: today(), motif: '', beneficiaire: '', piece_ref: '', service_id: '' });
      setSubmitted(false); setServerErr('');
    }
  }, [open, sens, op]);

  // modifying a sortie: its own amount is back in the cash
  const dispo = r2(solde + (op && op.sens === 'sortie' ? num(op.montant) : 0));
  const amount = r2(num(f.montant));
  const errors: Record<string, string> = {};
  if (!(amount > 0)) errors.montant = 'Indiquez un montant (ex : 250 ou 250,50).';
  else if (!E && amount > dispo) errors.montant = `Solde insuffisant : la caisse contient ${dh(dispo)}.`;
  if (!f.categorie) errors.categorie = "Choisissez le type d'opération.";
  if (!f.date) errors.date = 'Indiquez la date.';
  else if (f.date > today()) errors.date = 'La date ne peut pas être dans le futur.';
  else if (depuis && f.date < depuis) errors.date = `La caisse démarre le ${fDate(depuis)}.`;
  if (!f.motif.trim()) errors.motif = 'Le motif est obligatoire : il apparaît dans le journal.';
  const show = (k: string) => (submitted ? errors[k] : undefined);

  const save = async (print: boolean) => {
    setSubmitted(true); setServerErr('');
    if (Object.keys(errors).length) return;
    setSaving(true);
    try {
      const body = {
        categorie: f.categorie, montant: amount, date: f.date, motif: f.motif.trim(),
        beneficiaire: f.beneficiaire.trim() || null, piece_ref: f.piece_ref.trim() || null,
        service_id: f.service_id ? Number(f.service_id) : null,
      };
      const x = op ? await comptoirCaisseAPI.updateOperation(Number(op.id), body) : await comptoirCaisseAPI.createOperation({ sens, ...body });
      onSaved(x, print);
    } catch (e: any) { setServerErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };

  const color = E ? GREEN : RED;
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth aria-labelledby="caisse-op-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="caisse-op-title" icon={E ? <EntreeIcon /> : <SortieIcon />} color={color} onClose={onClose}
        title={op ? `Modifier ${op.reference}` : E ? 'Entrée de caisse' : 'Sortie de caisse'}
        sub={op ? `${E ? 'Entrée' : 'Sortie'} de caisse — l'ancien et le nouveau contenu sont gardés dans l'historique`
          : E ? "Argent qui entre dans la caisse — hors paiements clients (ils entrent tout seuls)" : 'Dépense payée en espèces'} />
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        <SoldeLine label="Solde actuel" sub="selon le journal" value={dh(solde)} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <NumberField label="Montant" required fullWidth autoFocus unit="DH" value={f.montant}
              onChange={(v) => setF({ ...f, montant: v })} error={!!show('montant')} helperText={show('montant') || ' '} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField select label="Type d'opération" required fullWidth value={f.categorie}
              onChange={(e) => setF({ ...f, categorie: e.target.value })} error={!!show('categorie')} helperText={show('categorie') || ' '}>
              {(E ? meta?.entrees : meta?.sorties)?.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Date" type="date" required fullWidth InputLabelProps={{ shrink: true }} value={f.date}
              inputProps={{ max: today(), min: depuis || undefined }} onChange={(e) => setF({ ...f, date: e.target.value })}
              error={!!show('date')} helperText={show('date') || ' '} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Motif" required fullWidth value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })}
              placeholder={E ? 'ex : alimentation de la caisse par la direction' : 'ex : 2 disques à tronçonner + gaz de soudage'}
              error={!!show('motif')} helperText={show('motif') || ' '} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label={E ? 'Remis par' : 'Bénéficiaire / fournisseur'} fullWidth value={f.beneficiaire}
              onChange={(e) => setF({ ...f, beneficiaire: e.target.value })} helperText="Facultatif" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="N° de pièce / facture" fullWidth value={f.piece_ref}
              onChange={(e) => setF({ ...f, piece_ref: e.target.value })} helperText="Facultatif" />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField select label="Service / projet concerné" fullWidth value={f.service_id}
              onChange={(e) => setF({ ...f, service_id: e.target.value })} helperText="Facultatif : l'opération apparaît aussi dans son historique">
              <MenuItem value=""><em>Aucun</em></MenuItem>
              {meta?.services.map((sv) => <MenuItem key={sv.id} value={String(sv.id)}>{sv.reference} · {sv.titre}</MenuItem>)}
              {/* a clôturé service is no longer offered, but stays on the operation being modified */}
              {op?.service_id && meta && !meta.services.some((sv) => sv.id === op.service_id) && (
                <MenuItem value={String(op.service_id)}>{op.service_reference}</MenuItem>
              )}
            </TextField>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 2, color: 'text.secondary' }}>
          <LockIcon sx={{ fontSize: 18, mt: '2px' }} />
          <Typography variant="body2">
            {op
              ? 'La modification est refusée si la caisse passait en négatif à une date : modifiez d\'abord les sorties concernées.'
              : "L'opération reçoit un numéro CAI. En cas d'erreur : « Annuler » dans le journal, avec un motif (elle reste visible, barrée), ou la modifier / supprimer avec la permission."}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => save(true)} disabled={saving} sx={{ borderColor: COLOR, color: COLOR }}>
          Enregistrer + bon
        </Button>
        <Button variant="contained" onClick={() => save(false)} disabled={saving} sx={{ ...primaryBtnSx, minWidth: 140 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- Comptage ---------------- */
export const ComptageDialog: React.FC<{
  open: boolean; solde: number; onClose: () => void; onSaved: (x: ComptoirCaisseMove) => void;
}> = ({ open, solde, onClose, onSaved }) => {
  const [montant, setMontant] = useState('');
  const [motif, setMotif] = useState('');
  const [regul, setRegul] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setMontant(''); setMotif(''); setRegul(false); setSubmitted(false); setServerErr(''); }
  }, [open]);

  const typed = montant.trim() !== '';
  const ecart = r2(num(montant) - solde);
  const err = submitted && !typed ? "Indiquez le montant compté (0 si la caisse est vide)." : '';
  const save = async () => {
    setSubmitted(true); setServerErr('');
    if (!typed) return;
    setSaving(true);
    try {
      onSaved(await comptoirCaisseAPI.createComptage({ montant: r2(num(montant)), motif: motif.trim() || null, regulariser: regul && ecart !== 0 }));
    } catch (e: any) { setServerErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth aria-labelledby="caisse-cpt-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="caisse-cpt-title" icon={<ComptageIcon />} color={COLOR} onClose={onClose}
        title="Comptage de caisse" sub="Comptez l'argent réellement présent dans la caisse" />
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        <SoldeLine label="Solde théorique" sub={`selon le journal, au ${fDate(today())}`} value={dh(solde)} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <NumberField label="Montant compté" required fullWidth autoFocus unit="DH" value={montant}
              onChange={setMontant} error={!!err} helperText={err || ' '} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Écart</Typography>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700, color: !typed ? 'text.secondary' : ecart ? 'error.main' : 'success.main' }}>
              {!typed ? '—' : `${ecart > 0 ? '+' : ''}${dh(ecart)}`}
            </Typography>
            {typed && (
              <Typography variant="caption" sx={{ color: ecart ? 'error.main' : 'success.main' }}>
                {ecart > 0 ? 'Excédent : plus d\'argent que prévu' : ecart < 0 ? 'Manquant : moins d\'argent que prévu' : 'Caisse juste'}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField label="Observation" fullWidth value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="ex : comptage de fin de journée" />
          </Grid>
        </Grid>
        {typed && ecart !== 0 && (
          <FormControlLabel sx={{ mt: 1.5, alignItems: 'flex-start' }}
            control={<Checkbox checked={regul} onChange={(e) => setRegul(e.target.checked)} sx={{ pt: 0.25 }} />}
            label={<Typography variant="body2">Régulariser l'écart dans le journal : une opération « Régularisation » de {dh(Math.abs(ecart))} ramène le solde au montant compté.</Typography>} />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving} sx={{ ...primaryBtnSx, minWidth: 200 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}>Enregistrer le comptage</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- Solde initial ---------------- */
export const OuvertureDialog: React.FC<{
  open: boolean; current: ComptoirCaisseOuverture | null; history: ComptoirCaisseOuverture[];
  onClose: () => void; onSaved: () => void;
}> = ({ open, current, history, onClose, onSaved }) => {
  const [montant, setMontant] = useState('');
  const [depuis, setDepuis] = useState(today());
  const [motif, setMotif] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setMontant(current ? String(num(current.montant)) : ''); setDepuis(current?.depuis || today());
      setMotif(''); setSubmitted(false); setServerErr('');
    }
  }, [open, current]);

  const errors: Record<string, string> = {};
  if (montant.trim() === '') errors.montant = 'Indiquez le montant présent (0 si la caisse est vide).';
  if (!depuis) errors.depuis = 'Indiquez la date.';
  else if (depuis > today()) errors.depuis = 'La date ne peut pas être dans le futur.';
  if (current && !motif.trim()) errors.motif = "Indiquez le motif de la modification : il est gardé dans l'historique.";
  const show = (k: string) => (submitted ? errors[k] : undefined);

  const save = async () => {
    setSubmitted(true); setServerErr('');
    if (Object.keys(errors).length) return;
    setSaving(true);
    try {
      await comptoirCaisseAPI.setOuverture({ montant: r2(num(montant)), depuis, motif: motif.trim() || null });
      onSaved();
    } catch (e: any) { setServerErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth aria-labelledby="caisse-ouv-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="caisse-ouv-title" icon={<OuvertureIcon />} color={COLOR} onClose={onClose}
        title={current ? 'Modifier le solde initial' : 'Solde initial de la caisse'} sub="Le point de départ du journal de caisse" />
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <NumberField label="Montant présent dans la caisse" required fullWidth autoFocus unit="DH" value={montant}
              onChange={setMontant} error={!!show('montant')} helperText={show('montant') || ' '} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="À la date du" type="date" required fullWidth InputLabelProps={{ shrink: true }} value={depuis}
              inputProps={{ max: today() }} onChange={(e) => setDepuis(e.target.value)} error={!!show('depuis')} helperText={show('depuis') || ' '} />
          </Grid>
          {current && (
            <Grid item xs={12}>
              <TextField label="Motif de la modification" required fullWidth value={motif} onChange={(e) => setMotif(e.target.value)}
                placeholder="ex : erreur de saisie à l'ouverture" error={!!show('motif')} helperText={show('motif') || ' '} />
            </Grid>
          )}
        </Grid>
        <Alert severity="info" sx={{ mt: 1 }}>
          Les paiements clients en espèces <b>antérieurs</b> à cette date ne sont pas ajoutés : ils sont déjà dans ce montant.
        </Alert>
        {history.length > 0 && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
            Historique : {history.map((h) => `${dh(h.montant)} au ${fDate(h.depuis)} (${h.user_name || '—'}${h.motif ? ` — ${h.motif}` : ''})`).join(' · ')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving} sx={{ ...primaryBtnSx, minWidth: 140 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- Annuler une opération ---------------- */
export const AnnulerCaisseDialog: React.FC<{
  open: boolean; op: ComptoirCaisseMove | null; onClose: () => void; onSaved: (x: ComptoirCaisseMove) => void;
}> = ({ open, op, onClose, onSaved }) => {
  const [motif, setMotif] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setMotif(''); setSubmitted(false); setServerErr(''); } }, [open]);
  if (!op) return null;
  const err = submitted && !motif.trim() ? "Le motif de l'annulation est obligatoire." : '';
  const save = async () => {
    setSubmitted(true); setServerErr('');
    if (!motif.trim()) return;
    setSaving(true);
    try { onSaved(await comptoirCaisseAPI.annuler(Number(op.id), motif.trim())); }
    catch (e: any) { setServerErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth aria-labelledby="caisse-ann-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="caisse-ann-title" icon={<UndoIcon />} color={ORANGE} onClose={onClose}
        title={`Annuler ${op.reference}`} sub="L'opération reste dans le journal, barrée, avec ce motif" />
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        <SoldeLine label={op.libelle} sub={`${fDate(op.date)}${op.motif ? ` · ${op.motif}` : ''}`}
          value={dh(op.montant)} />
        <TextField label="Motif de l'annulation" required fullWidth autoFocus value={motif} onChange={(e) => setMotif(e.target.value)}
          placeholder="ex : saisie en double, mauvais montant" error={!!err} helperText={err || ' '} />
        <Typography variant="body2" color="text.secondary">
          {op.sens === 'comptage' ? 'Le comptage reste visible, barré.' : 'Le solde est recalculé sans elle.'} Saisissez ensuite la bonne opération si besoin.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Fermer</Button>
        <Button variant="contained" color="warning" onClick={save} disabled={saving} sx={{ minWidth: 200 }}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <UndoIcon />}>Annuler l'opération</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- Supprimer une opération ---------------- */
export const SupprimerCaisseDialog: React.FC<{
  open: boolean; op: ComptoirCaisseMove | null; impact: ComptoirCaisseImpact | null; onClose: () => void; onDeleted: (reference: string) => void;
}> = ({ open, op, impact, onClose, onDeleted }) => {
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setServerErr(''); }, [open]);
  if (!op || !impact) return null;
  const blocked = !impact.supprimable;
  const remove = async () => {
    setServerErr(''); setSaving(true);
    try { onDeleted((await comptoirCaisseAPI.deleteOperation(Number(op.id))).reference); }
    catch (e: any) { setServerErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth aria-labelledby="caisse-del-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="caisse-del-title" icon={<DeleteIcon />} color={RED} onClose={onClose}
        title={`Supprimer ${op.reference}`} sub={blocked ? 'Suppression impossible pour le moment' : "L'opération quitte le journal ; son numéro n'est pas réutilisé"} />
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        <SoldeLine label={op.libelle} sub={`${fDate(op.date)}${op.motif ? ` · ${op.motif}` : ''}`} value={dh(op.montant)} />
        {blocked ? (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>À supprimer ou corriger d'abord :</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {impact.blocages.map((b) => <li key={b}><Typography variant="body2">{b}</Typography></li>)}
            </Box>
          </Alert>
        ) : (
          <Alert severity="info">
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {impact.avertissements.map((a) => <li key={a}><Typography variant="body2">{a}</Typography></li>)}
              <li><Typography variant="body2">La suppression est notée dans l'historique (qui, quand, quel montant).</Typography></li>
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Fermer</Button>
        {!blocked && (
          <Button variant="contained" color="error" onClick={remove} disabled={saving} sx={{ minWidth: 200 }}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}>Supprimer l'opération</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- Paiement client (BON…): managed on its service / offre ---------------- */
export const PaiementCaisseDialog: React.FC<{
  open: boolean; op: ComptoirCaisseMove | null; action: 'edit' | 'delete'; canOpen: boolean; onClose: () => void;
}> = ({ open, op, action, canOpen, onClose }) => {
  const navigate = useNavigate();
  if (!op) return null;
  const verb = action === 'edit' ? 'modifier' : 'supprimer';
  const parts = op.rattachements || [];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="caisse-pay-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Head id="caisse-pay-title" icon={<InfoIcon />} color={ORANGE} onClose={onClose}
        title={`${action === 'edit' ? 'Modifier' : 'Supprimer'} ${op.reference}`} sub="Paiement client en espèces" />
      <DialogContent sx={{ pt: '20px !important' }}>
        <SoldeLine label={op.libelle} sub={`${fDate(op.date)}${op.client_nom ? ` · ${op.client_nom}` : ''}`} value={dh(op.montant)} />
        <Alert severity="warning" sx={{ mb: 2 }}>
          Ce paiement n'est pas une opération de caisse : il entre tout seul depuis « Encaisser ». Pour le {verb}, passez par le
          service / projet ou l'offre où il a été encaissé — la caisse suit automatiquement.
        </Alert>
        <Stack spacing={1}>
          {parts.map((a, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {a.service_reference || '—'}{a.offre_reference ? ` · offre ${a.offre_reference}` : ''}
                </Typography>
                <Typography variant="caption" color={a.annule || a.supprime ? 'warning.main' : 'text.secondary'}>
                  {a.supprime
                    ? `Offre supprimée : cette part (${dh(a.montant)}) est annulée et rendue au client par une sortie « Remboursement client ».`
                    : a.annule
                      ? `Part annulée (${dh(a.montant)}).`
                      : `${a.type} · ${dh(a.montant)}`}
                </Typography>
              </Box>
              {canOpen && a.service_id && !a.supprime && (
                <Button size="small" variant="outlined" endIcon={<OpenIcon />} onClick={() => navigate(`/comptoir/services/${a.service_id}`)}
                  sx={{ borderColor: COLOR, color: COLOR, whiteSpace: 'nowrap' }}>
                  Ouvrir {a.service_reference}
                </Button>
              )}
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit">Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};
